import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Cow, Eye, EyeSlash } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    setError("");
    setLoading(true);
    const cleanEmail = email.trim().toLowerCase();
    const cleanPwd = password.trim();
    const res = await login(cleanEmail, cleanPwd);
    setLoading(false);
    if (res.ok) {
      toast.success("Bem-vindo!");
      if (res.must_change_password) {
        nav("/trocar-senha", { replace: true });
      } else {
        const to = location.state?.from?.pathname || "/dashboard";
        nav(to, { replace: true });
      }
    } else {
      setError(res.error);
    }
  }

  return (
    <div className="min-h-screen grid md:grid-cols-2 bg-[#F6F5F2]">
      {/* Left: brand + image (hidden on mobile) */}
      <div
        className="hidden md:block relative bg-cover bg-center"
        style={{
          backgroundImage:
            'url("https://images.pexels.com/photos/1276237/pexels-photo-1276237.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1000&w=1200")',
        }}
      >
        <div className="absolute inset-0 bg-[#1A1C19]/55" />
        <div className="relative z-10 h-full flex flex-col justify-between p-12 text-white">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-sm bg-[#2A5A3B] flex items-center justify-center">
              <Cow size={22} color="#fff" weight="duotone" />
            </div>
            <div>
              <div className="font-heading font-black text-2xl leading-none tracking-tight">
                CEDUP Leite
              </div>
              <div className="text-[11px] uppercase tracking-[0.25em] mt-1">
                Gestão de pecuária de leite
              </div>
            </div>
          </div>
          <div>
            <h1 className="font-heading font-black text-4xl leading-tight tracking-tighter">
              Do curral ao<br /> painel de controle.
            </h1>
            <p className="mt-4 text-sm leading-relaxed max-w-md opacity-90">
              Registre toda a cadeia da pecuária leiteira: animais, cio,
              inseminações, partos, tratamentos e produção individual em uma
              única plataforma pensada para a propriedade rural.
            </p>
          </div>
          <div className="text-[11px] uppercase tracking-[0.25em] opacity-70">
            v1.0 · seguro e auditável · pwa
          </div>
        </div>
      </div>

      {/* Right: form */}
      <div className="flex items-center justify-center p-6 md:p-12">
        <div className="w-full max-w-md">
          {/* Mobile brand hero */}
          <div
            className="md:hidden relative mb-8 -mx-6 -mt-6 h-40 bg-cover bg-center flex items-end"
            style={{
              backgroundImage:
                'url("https://images.pexels.com/photos/1276237/pexels-photo-1276237.jpeg?auto=compress&cs=tinysrgb&h=600")',
            }}
          >
            <div className="absolute inset-0 bg-[#1A1C19]/60" />
            <div className="relative z-10 p-5 text-white">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-sm bg-[#2A5A3B] flex items-center justify-center">
                  <Cow size={20} color="#fff" weight="duotone" />
                </div>
                <span className="font-heading font-black text-xl">CEDUP Leite</span>
              </div>
              <p className="text-xs opacity-90 mt-2">
                Gestão de pecuária de leite
              </p>
            </div>
          </div>

          <h2 className="font-heading font-black text-3xl tracking-tight">
            Entrar
          </h2>
          <p className="text-sm text-[#4E544B] mt-2">
            Use suas credenciais fornecidas pelo administrador.
          </p>

          <form onSubmit={handleSubmit} className="mt-8 space-y-5">
            <div>
              <label
                htmlFor="login-email-input"
                className="text-xs font-bold uppercase tracking-[0.15em] text-[#4E544B] block mb-2"
              >
                Email
              </label>
              <input
                id="login-email-input"
                data-testid="login-email"
                type="email"
                required
                autoComplete="username"
                inputMode="email"
                autoCapitalize="none"
                spellCheck="false"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full bg-white border border-[#E2E0D8] rounded-sm px-4 py-3 text-base focus:outline-none focus:border-[#2A5A3B]"
                placeholder="admin@fazenda.com"
              />
            </div>
            <div>
              <label
                htmlFor="login-password-input"
                className="text-xs font-bold uppercase tracking-[0.15em] text-[#4E544B] block mb-2"
              >
                Senha
              </label>
              <div className="relative">
                <input
                  id="login-password-input"
                  data-testid="login-password"
                  type={showPwd ? "text" : "password"}
                  required
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full bg-white border border-[#E2E0D8] rounded-sm px-4 py-3 pr-12 text-base focus:outline-none focus:border-[#2A5A3B]"
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  data-testid="toggle-password"
                  onClick={() => setShowPwd((s) => !s)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#4E544B] p-1"
                  aria-label={showPwd ? "Ocultar senha" : "Mostrar senha"}
                >
                  {showPwd ? <EyeSlash size={18} weight="duotone" /> : <Eye size={18} weight="duotone" />}
                </button>
              </div>
            </div>
            {error && (
              <div
                data-testid="login-error"
                className="text-sm text-[#C05746] bg-[#FDEDED] border border-[#C05746]/20 rounded-sm px-3 py-2"
              >
                {error}
              </div>
            )}
            <button
              data-testid="login-submit"
              disabled={loading}
              className="w-full bg-[#2A5A3B] hover:bg-[#1E412A] disabled:opacity-60 text-white font-bold tracking-wide rounded-sm px-6 py-3.5 transition-colors text-base"
            >
              {loading ? "Entrando…" : "Entrar"}
            </button>
          </form>

          <div className="mt-8 text-[11px] text-[#4E544B] uppercase tracking-[0.2em]">
            Precisa de acesso? Fale com o administrador da propriedade.
          </div>
        </div>
      </div>
    </div>
  );
}
